import { get } from 'lodash-es';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Icon,
  NumberInput,
  NumberInputProps,
  Text,
  Tooltip,
  useConfiguredForm,
} from '../index';
import { FormFeedback, FormGroup, Label } from './common';
import clsx from 'clsx';

type NumberFieldProps<TKeys extends string> = NumberInputProps & {
  translate: (key: TKeys) => string;
  feedbackType?: 'tooltip' | 'inline';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toNumber = (value: any) => {
  let nextValue: number | null = null;
  const maybeNumber = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isFinite(maybeNumber) && !Number.isNaN(maybeNumber)) {
    nextValue = maybeNumber;
  }
  return nextValue;
};
const NumberField = <TKeys extends string>({
  label,
  helperText,
  translate,
  onChangeValue,
  theme,
  withKnobs = true,
  feedbackType = 'inline',
  tooltips,
  ...props
}: NumberFieldProps<TKeys>) => {
  const methods = useFormContext();
  const configurationContext = useConfiguredForm();
  const errorMessage = get(
    methods.formState.errors,
    `${props.name}.message`,
  ) as unknown as TKeys;
  const watchedValue = useWatch({
    name: props.name,
    defaultValue: props.defaultValue,
  }) as undefined | number;
  const register = methods.register(props.name, {
    setValueAs: (value) => {
      return toNumber(value);
    },
    onChange(event: React.FormEvent<HTMLInputElement>) {
      onChangeValue?.(toNumber(event.currentTarget.value));
    },
  });
  return (
    <FormGroup className='w-100'>
      {label && (
        <div className='space-x-2 position-relative'>
          {tooltips && (
            <div className='position-absolute'>
              <Tooltip placement='right' label={tooltips}>
                <Icon icon={'info-circle'} width={13} height={13} />
              </Tooltip>
            </div>
          )}
          <Label
            htmlFor={props.id || props.name}
            className={clsx({
              'ms-3': tooltips,
              'required': props.required,
            })}
          >
            {label}
          </Label>
        </div>
      )}
      {configurationContext.mode === 'WRITE' ? (
        <NumberInput
          {...props}
          withKnobs={withKnobs}
          onChangeValue={(value) => {
            methods.setValue(props.name, value, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
            onChangeValue?.(value);
          }}
          isInvalid={!!errorMessage}
          {...register}
          value={watchedValue}
          min={props.min}
          max={props.max}
        />
      ) : (
        <Text heading='normal' weight='semi-bold'>
          {methods.getValues(props.name) ?? '-'}
        </Text>
      )}

      {errorMessage ? (
        <FormFeedback
          invalid
          tooltip={feedbackType === 'tooltip'}
          id={`${props.name}.error`}
        >
          {translate(errorMessage)}
        </FormFeedback>
      ) : helperText ? (
        <FormFeedback>{helperText}</FormFeedback>
      ) : null}
    </FormGroup>
  );
};

export default NumberField;
